<template>
  <section class="font-sans text-[#1D1D1F] bg-[#FBFBFD] overflow-hidden antialiased">
    
    <div class="relative pt-32 pb-20 px-6 text-center" data-aos="fade">
      <div class="inline-block px-4 py-1.5 mb-6 rounded-full bg-blue-50 text-blue-600 text-[11px] font-bold uppercase tracking-[0.2em]" data-aos="fade-down">
        Excellence Linguistique
      </div>
      <h1 class="text-4xl lg:text-6xl font-bold tracking-tighter leading-tight max-w-4xl mx-auto" data-aos="fade-up">
        Centre Linguistique <br/> <span class="bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">NovaLingua</span>
      </h1>
      <p class="mt-8 text-[#86868B] text-lg lg:text-xl max-w-3xl mx-auto font-light leading-relaxed" data-aos="fade-up" data-aos-delay="100">
        Un espace d’apprentissage moderne situé à <span class="text-[#1D1D1F] font-medium">Bafoussam</span> et <span class="text-[#1D1D1F] font-medium">Dschang</span>. 
        Maîtrisez l’allemand, l’anglais, le français et l’italien du niveau A1 à C1.
      </p>
    </div>

    <div class="container mx-auto px-6 lg:px-20 py-10">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div
          v-for="(service, index) in services"
          :key="index"
          @click="toggleService(index)"
          class="ios-service-card group cursor-pointer"
          :class="{ 'ring-2 ring-blue-500/20 shadow-xl': selectedService === index }"
          data-aos="fade-up"
          :data-aos-delay="index * 100"
        >
          <div class="w-12 h-12 flex items-center justify-center rounded-xl bg-[#F5F5F7] text-blue-600 mb-8 transition-colors group-hover:bg-blue-600 group-hover:text-white">
            <component :is="service.icon" class="w-6 h-6" />
          </div>

          <h3 class="text-xl font-bold mb-4 tracking-tight text-[#1D1D1F]">
            {{ service.titleKey }}
          </h3>
          <p class="text-[#86868B] text-[15px] leading-relaxed font-light mb-4">
            {{ service.descKey }}
          </p>

          <div v-if="service.details || service.schools || service.universities" class="flex items-center text-blue-600 text-[13px] font-semibold">
            <span class="mr-2">{{ selectedService === index ? 'Réduire' : 'En savoir plus' }}</span>
            <svg class="w-4 h-4 transition-transform duration-300" :class="{'rotate-180': selectedService === index}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          <transition name="expand">
            <div v-if="selectedService === index" class="mt-6 pt-6 border-t border-[#F5F5F7] overflow-hidden">
              
              <div v-if="service.details">
                <div v-for="(category, idx) in service.details" :key="idx" class="mb-5 last:mb-0">
                  <h4 class="text-xs font-bold uppercase tracking-widest text-[#1D1D1F] mb-3 flex items-center">
                     <span class="mr-2">{{ category.flag }}</span> {{ category.lang }}
                  </h4>
                  <div class="flex flex-wrap gap-2">
                    <span v-for="test in category.tests" :key="test" class="px-2.5 py-1 bg-white border border-[#E5E5E7] text-[#424245] text-[11px] rounded-full shadow-sm">
                      {{ test }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="service.schools" class="space-y-4">
                <p class="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2">10 Écoles reconnues en Allemagne</p>
                <div v-for="(school, sIdx) in service.schools" :key="sIdx" class="text-left bg-[#FBFBFD] p-3 rounded-xl border border-gray-100">
                  <p class="text-sm font-bold text-[#1D1D1F]">{{ school.name }}</p>
                  <p class="text-[12px] text-[#86868B] leading-snug mt-1">{{ school.desc }}</p>
                </div>
              </div>

              <div v-if="service.universities" class="space-y-2">
                <p class="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2">20 Universités d'Excellence</p>
                <div v-for="(uni, uIdx) in service.universities" :key="uIdx" class="flex gap-2 items-start text-left text-[13px]">
                  <span class="text-blue-500 font-bold">{{ uIdx + 1 }}.</span>
                  <p class="text-[#424245]"><span class="font-semibold text-[#1D1D1F]">{{ uni.name }}</span> – {{ uni.specialty }}</p>
                </div>
              </div>

            </div>
          </transition>
        </div>
      </div>
    </div>

    </section>
</template>

<script setup>
import { ref, defineComponent } from 'vue';

// --- Icons ---
const IconTeacher = defineComponent({ template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M12 14l9-5-9-5-9 5 9 5z" /><path d="M12 14l6.16-3.422A12.083 12.083 0 0118 14.5c0 4.142-3.358 7.5-7.5 7.5S3 18.642 3 14.5a12.083 12.083 0 01-.16-3.922L12 14z" /></svg>` });
const IconTravel = defineComponent({ template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 002 2 2 2 0 012 2v.653M3 12a9 9 0 1118 0 9 9 0 01-18 0z" /></svg>` });
const IconUniversity = defineComponent({ template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>` });
const IconSupport = defineComponent({ template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>` });
const IconLibrary = defineComponent({ template: `<svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>` });

const selectedService = ref(null);

const toggleService = (index) => {
  if (services[index].details || services[index].schools || services[index].universities) {
    selectedService.value = selectedService.value === index ? null : index;
  }
};

const services = [
  { 
    icon: IconTeacher, 
    titleKey: "Formations", 
    descKey: "Accès aux meilleures écoles de formation professionnelle en Allemagne.",
    schools: [
      { name: "1. IHK (Chambre de Commerce)", desc: "Référence nationale pour le commerce, l'industrie et le management." },
      { name: "2. HWK (Chambre des Métiers)", desc: "Électricité, mécanique, bâtiment et titres de Meister." },
      { name: "3. Berufsschule", desc: "École d'État liée au système duale Ausbildung." },
      { name: "4. Fachschule", desc: "Formation technique avancée (Staatlich geprüfter Techniker)." },
      { name: "5. Berufsfachschule", desc: "Santé, social, informatique et assistant médical." },
      { name: "6. Fachoberschule (FOS)", desc: "Profil technique et économique + accès supérieur." },
      { name: "7. Berufliches Gymnasium", desc: "Lycée pro combinant Abitur et technique." },
      { name: "8. Dualen Hochschulen", desc: "Alternance entreprise + école de niveau universitaire." },
      { name: "9. TÜV Akademie", desc: "Industrie, sécurité et management de la qualité." },
      { name: "10. DEKRA Akademie", desc: "Logistique, transport et reconversion IT." }
    ]
  },
  { 
    icon: IconTravel, 
    titleKey: "International", 
    descKey: "Préparation aux tests et assistance complète pour vos procédures de visa.",
    details: [
      { flag: "🇩🇪", lang: "Allemand", tests: ["Goethe-Zertifikat", "TestDaF", "ECL", "ÖSD", "telc"] },
      { flag: "🇬🇧", lang: "Anglais", tests: ["IELTS", "TOEFL", "Cambridge", "TOEIC"] },
      { flag: "🇮🇹", lang: "Italien", tests: ["CILS", "CELI", "PLIDA"] }
    ]
  },
  { 
    icon: IconUniversity, 
    titleKey: "Partenariats", 
    descKey: "Accès direct aux 20 universités les plus prestigieuses d'Allemagne.",
    universities: [
      { name: "TU München (TUM)", specialty: "Ingénierie & Technologie (Top Allemagne)" },
      { name: "LMU München", specialty: "Sciences, lettres, médecine" },
      { name: "Heidelberg University", specialty: "Histoire et recherche" },
      { name: "Freie Universität Berlin", specialty: "Sciences sociales" },
      { name: "Humboldt-Berlin", specialty: "Sciences humaines" },
      { name: "KIT Karlsruhe", specialty: "Technologie appliquée" },
      { name: "RWTH Aachen", specialty: "Ingénierie de pointe" },
      { name: "TU Berlin", specialty: "Informatique & Ingénierie" },
      { name: "Freiburg University", specialty: "Médecine & Sciences" },
      { name: "Bonn University", specialty: "Économie & Sciences" },
      { name: "Hamburg University", specialty: "Multidisciplinaire" },
      { name: "Mannheim University", specialty: "Gestion & Business" },
      { name: "Goethe-Frankfurt", specialty: "Finance & Économie" },
      { name: "Stuttgart University", specialty: "Architecture & Ingénierie" },
      { name: "Leipzig University", specialty: "Linguistique" },
      { name: "Cologne University", specialty: "Social & Économie" },
      { name: "TU Dresden", specialty: "Sciences & Ingénierie" },
      { name: "BTU Cottbus", specialty: "Technologies" },
      { name: "Jena University", specialty: "Natural Sciences" },
      { name: "Hohenheim", specialty: "Agriculture & Sciences de la vie" }
    ]
  },
  { icon: IconSupport, titleKey: "Suivi individuel", descKey: "Évaluation continue et coaching personnalisé pour chaque apprenant." },
  { icon: IconLibrary, titleKey: "Multimédia", descKey: "Ressources numériques interactives et bibliothèque moderne accessible." },
];
</script>

<style scoped>
/* Typographie & Cards (Idem code précédent) */
.font-sans {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  letter-spacing: -0.011em;
}
.ios-service-card {
  @apply bg-white p-10 rounded-[2.5rem] shadow-[0_4px_24px_rgba(0,0,0,0.04)] 
         border border-[#F5F5F7] transition-all duration-500 
         hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1;
}
.expand-enter-active, .expand-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 1000px; /* Augmenté pour contenir la liste longue */
}
.expand-enter-from, .expand-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}
</style>